import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";

const Loader = ({ onComplete }) => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    document.body.style.overflow = "hidden";

    let animationFrameId;
    const startTime = performance.now();
    const duration = 900; // Smooth 0.9s count from 00% to 100%

    const tick = (now) => {
      const elapsed = now - startTime;
      const progressRatio = Math.min(elapsed / duration, 1);
      // Smooth cubic-out easing curve
      const eased = 1 - Math.pow(1 - progressRatio, 3);
      const currentVal = Math.round(eased * 100);
      setProgress(currentVal);

      if (progressRatio < 1) {
        animationFrameId = requestAnimationFrame(tick);
      } else {
        setTimeout(() => {
          document.body.style.overflow = "auto";
          if (onComplete) onComplete();
        }, 100);
      }
    };

    animationFrameId = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(animationFrameId);
      document.body.style.overflow = "auto";
    };
  }, [onComplete]);

  // Format with leading zero: "00", "06", ... "100"
  const formattedNumber = progress < 10 ? `0${progress}` : `${progress}`;

  return (
    <motion.div
      initial={{ opacity: 1 }}
      exit={{ opacity: 0, y: -20, filter: "blur(6px)" }}
      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      className="fixed inset-0 z-[999999] flex flex-col items-center justify-center bg-[#0F172A] text-white select-none overflow-hidden"
    >
      {/* Ambient background glow (Blue & #00B4DB Accent) */}
      <div className="absolute w-[28rem] h-[28rem] rounded-full bg-blue-600/15 blur-[120px] pointer-events-none" />
      <div className="absolute w-[20rem] h-[20rem] rounded-full bg-[#00B4DB]/15 blur-[100px] pointer-events-none" />

      <div className="relative z-10 flex flex-col items-center">
        {/* Name */}
        <h2 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight mb-2">
          Partha
        </h2>

        {/* Big Percentage Display with Blue Gradient Accent */}
        <div className="text-6xl sm:text-7xl font-black text-white tracking-tight mb-3 flex items-baseline select-none">
          <span>{formattedNumber}</span>
          <span className="text-4xl sm:text-5xl font-bold ml-2 text-[#00B4DB]">%</span>
        </div>

        {/* Minimal Progress Bar Underneath (Blue to #00B4DB Gradient) */}
        <div className="w-48 sm:w-56 h-[2.5px] bg-slate-800 rounded-full overflow-hidden relative border border-slate-700/60 shadow-inner">
          <div
            className="h-full bg-gradient-to-r from-blue-600 via-sky-500 to-[#00B4DB] rounded-full transition-all duration-75 shadow-md shadow-[#00B4DB]/30"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
    </motion.div>
  );
};

export default Loader;
